import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import {
  isMasterContext,
  getNotifications,
  getUnreadNotificationCount,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  deleteNotification
} from '../services/authService';
import './NotificationBell.css';

const POLL_INTERVAL = 30000; // 30 seconds

function formatRelativeTime(dateString) {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);

  if (diffSec < 60) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  return date.toLocaleDateString();
}

/**
 * The accent colour for a notification'''s type dot.
 *
 * Exported so it can be asserted directly (ORG-26, #321). It cannot be tested through the
 * rendered DOM: jsdom drops `var()` values from inline styles altogether — the style
 * attribute comes back `null` — so `toHaveStyle({ backgroundColor: '''var(--color-info)''' })`
 * passes whatever the switch returns, including for a type the switch does not name. That
 * was measured, not assumed: the assertion stayed green with all four `case` labels deleted.
 */
export function getTypeColor(type) {
  switch (type) {
    case 'FORM_APPROVED':
      return 'var(--color-success)';
    case 'FORM_REJECTED':
    case 'POLICY_OVERDUE':
    case 'DEADLINE_ESCALATION':
      return 'var(--color-error)';
    // DOC-23 (#471): a review task is work waiting on this person, as an approval is.
    case 'DOCUMENT_REVIEW_REQUESTED':
    case 'APPROVAL_NEEDED':
    case 'DEADLINE_REMINDER':
    case 'POLICY_ACKNOWLEDGEMENT_DUE':
      return 'var(--color-warning)';
    // Informational: something happened that is worth knowing and needs nothing done about
    // it. The four organizational types (ORG-26, #321) all land here — none is an action item
    // and none is overdue, so none takes --color-warning or --color-error. So does a review
    // workflow's update to its starter (DOC-23, #471): closed, or stalled with nobody to act.
    case 'DOCUMENT_REVIEW_UPDATED':
    case 'FORM_RETURNED':
    case 'FORM_DELEGATED':
    case 'ASSIGNMENT_CHANGED':
    case 'GROUP_LEADERSHIP_CHANGED':
    case 'DETAIL_STARTED':
    case 'DETAIL_ENDED':
      return 'var(--color-info)';
    default:
      return 'var(--color-text-secondary)';
  }
}

export default function NotificationBell() {
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const bellRef = useRef(null);
  const navigate = useNavigate();

  // Notifications live in tenant schemas, so under a master claim every call
  // here answers 403 TENANT_NOT_SELECTED — and the interceptor's correct
  // recovery for an INITIAL_ADMIN is to route to /select-agency. A background
  // poll would therefore eject the admin from the agency CRM every 30 seconds.
  // There is nothing to show in master context anyway.
  const suppressed = isMasterContext();

  const fetchUnreadCount = useCallback(async () => {
    try {
      const data = await getUnreadNotificationCount();
      setUnreadCount(data.count);
    } catch {
      // Silently fail — polling will retry
    }
  }, []);

  // Poll unread count
  useEffect(() => {
    if (suppressed) return undefined;
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchUnreadCount, suppressed]);

  // Fetch full list when dropdown opens
  useEffect(() => {
    if (isOpen && !suppressed) {
      setLoading(true);
      getNotifications({ page: 0, size: 50 })
        .then(data => setNotifications(data.content || []))
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [isOpen, suppressed]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target) &&
        bellRef.current && !bellRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleBellClick = () => {
    setIsOpen(prev => !prev);
  };

  if (suppressed) return null;

  const handleNotificationClick = async (notification) => {
    if (!notification.isRead) {
      try {
        await markNotificationAsRead(notification.id);
        setNotifications(prev =>
          prev.map(n => n.id === notification.id ? { ...n, isRead: true } : n)
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch {
        // Continue with navigation even if marking fails
      }
    }
    if (notification.link) {
      navigate(notification.link);
      setIsOpen(false);
    }
  };

  const handleMarkAllRead = async (e) => {
    e.stopPropagation();
    try {
      await markAllNotificationsAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch {
      // Silently fail
    }
  };

  const handleDelete = async (e, notificationId) => {
    e.stopPropagation();
    try {
      await deleteNotification(notificationId);
      setNotifications(prev => {
        const removed = prev.find(n => n.id === notificationId);
        if (removed && !removed.isRead) {
          setUnreadCount(c => Math.max(0, c - 1));
        }
        return prev.filter(n => n.id !== notificationId);
      });
    } catch {
      // Silently fail
    }
  };

  return (
    <div className="notification-bell-wrapper">
      <button
        ref={bellRef}
        className="notification-bell-button"
        onClick={handleBellClick}
        aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="notification-badge" aria-hidden="true">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div ref={dropdownRef} className="notification-dropdown" role="dialog" aria-label="Notifications">
          <div className="notification-dropdown-header">
            <span className="notification-dropdown-title">Notifications</span>
            {unreadCount > 0 && (
              <button className="notification-mark-all" onClick={handleMarkAllRead}>
                Mark all as read
              </button>
            )}
          </div>

          <div className="notification-dropdown-list">
            {loading && (
              <div className="notification-loading">Loading...</div>
            )}

            {!loading && notifications.length === 0 && (
              <div className="notification-empty">No notifications</div>
            )}

            {!loading && notifications.map(notification => (
              <div
                key={notification.id}
                className={`notification-item${!notification.isRead ? ' notification-item--unread' : ''}`}
                onClick={() => handleNotificationClick(notification)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') handleNotificationClick(notification); }}
              >
                <span
                  className="notification-type-dot"
                  style={{ backgroundColor: getTypeColor(notification.type) }}
                  aria-hidden="true"
                />
                <div className="notification-content">
                  <div className="notification-title">{notification.title}</div>
                  <div className="notification-message">{notification.message}</div>
                  <div className="notification-time">{formatRelativeTime(notification.createdAt)}</div>
                </div>
                <button
                  className="btn btn-danger btn-icon btn-sm btn-reveal notification-delete"
                  onClick={(e) => handleDelete(e, notification.id)}
                  aria-label={`Delete notification: ${notification.title}`}
                >
                  &times;
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
